import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Snackbar,
  useTheme,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { AIExpert } from '@garbera/shared';
import { useChat } from '../../hooks/useChat';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useExpertAI } from '../../hooks/useExpertAI';
import { DEFAULT_EXPERTS } from '../../types/ai';
import { AISelector } from './AISelector';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { WelcomeScreen } from './WelcomeScreen';
import { SalesProfilePromptBanner } from './SalesProfilePromptBanner';

interface ChatContainerProps {
  initialExpertId?: number;
  selectedSessionId?: number | null;
  onSessionChange?: (sessionId: number | null) => void;
  onViewChange?: (view: 'welcome' | 'chat') => void;
}

/**
 * チャットコンテナコンポーネント
 * チャット機能全体を統合し、状態管理を行う
 */
export const ChatContainer: React.FC<ChatContainerProps> = ({
  initialExpertId,
  selectedSessionId,
  onSessionChange,
  onViewChange,
}) => {
  const theme = useTheme();
  const [selectedExpert, setSelectedExpert] = useState<AIExpert | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputHeight, setInputHeight] = useState(96); // 初期の入力欄高さ
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<number | undefined>(undefined); // 再生成中のメッセージID
  
  // チャット履歴の更新用
  const { refreshChatHistory } = useChatHistory();

  // 専門家AI取得用hook
  const { 
    experts, 
    isLoading: expertsLoading, 
    error: expertsError, 
    loadExperts,
    clearError: clearExpertsError 
  } = useExpertAI();

  // スナックバー表示
  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // フォールバック用モック専門家データ（APIが利用できない場合）
  const fallbackExperts: AIExpert[] = DEFAULT_EXPERTS.map((expert, index) => ({
    ...expert,
    id: index + 1,
    createdBy: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));

  // 表示用の専門家リスト（実際のデータまたはフォールバック）
  const displayExperts = experts.length > 0 ? experts : fallbackExperts;

  // チャット管理フック
  const {
    session,
    messages,
    messageStatuses,
    isLoading,
    isTyping,
    currentRequestId,
    isAborting,
    error,
    sendMessage,
    startNewSession,
    abortMessage,
    clearError,
    clearMessages,
    retryMessage,
    regenerateAIMessage,
    retryAbortedMessage,
    loadChatHistory,
    loadMessages, // メッセージ履歴読み込み機能を追加
  } = useChat({
    expertId: selectedExpert?.id,
    sessionId: selectedSessionId || undefined,
    enableMockResponses: false,
  });

  // メッセージ再送信の処理（ユーザーメッセージ）またはAI再生成の処理
  const handleRetryMessage = useCallback(async (messageId: number) => {
    if (!session?.id) {
      showSnackbar('セッションが見つかりません', 'error');
      return;
    }

    try {
      // メッセージの種類を判定
      const message = messages.find(m => m.id === messageId);
      
      if (!message) {
        showSnackbar('メッセージが見つかりません', 'error');
        return;
      }

      if (message.messageType === 'user') {
        // ユーザーメッセージの再送信
        if (selectedExpert) {
          await retryMessage(messageId, selectedExpert, session.id);
          showSnackbar('メッセージを再送信しました');
        }
      } else if (message.messageType === 'assistant') {
        // AIメッセージの再生成開始
        setRegeneratingMessageId(messageId);
        
        try {
          await regenerateAIMessage(messageId, session.id);
          showSnackbar('AIメッセージを再生成しました');
          // チャット履歴を更新（メッセージ内容が変更されるため）
          await refreshChatHistory();
        } finally {
          // 再生成完了後に状態をリセット
          setRegeneratingMessageId(undefined);
        }
      }
    } catch (error) {
      console.error('Failed to retry/regenerate message:', error);
      showSnackbar('メッセージの処理に失敗しました', 'error');
      // エラー時も状態をリセット
      setRegeneratingMessageId(undefined);
    }
  }, [retryMessage, regenerateAIMessage, selectedExpert, session?.id, messages, showSnackbar, refreshChatHistory]);

  // 専門家選択時の処理（ウェルカム画面から）
  const handleExpertSelect = async (expert: AIExpert) => {
    try {
      setSelectedExpert(expert);
      await startNewSession(expert);
      
      if (onSessionChange) {
        onSessionChange(session?.id || null);
      }
      
      // 新しいセッションが作成されたので、チャット履歴を即座に更新
      await refreshChatHistory();
      
      showSnackbar(`${expert.name}との新しいセッションを開始しました`);
    } catch (error) {
      console.error('Failed to start new session:', error);
      showSnackbar('セッションの開始に失敗しました', 'error');
    }
  };

  // メッセージ送信時の処理
  const handleSendMessage = async (content: string, files?: File[]) => {
    try {
      const previousSessionId = session?.id;
      
      await sendMessage(content, files);
      
      // セッションが新規作成された場合（Geminiパターン）、履歴を更新
      const currentSessionId = session?.id;
      if (!previousSessionId && currentSessionId) {
        // 新しいセッションが作成されたので、チャット履歴を即座に更新
        await refreshChatHistory();
        
        // onSessionChangeコールバックを呼び出し
        if (onSessionChange) {
          onSessionChange(currentSessionId);
        }
        
        showSnackbar('新しいチャットセッションが開始されました');
      } else if (currentSessionId) {
        // 既存セッションでのメッセージ送信後、チャット履歴を更新（最終更新時刻や最後のメッセージが変更されるため）
        await refreshChatHistory();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      showSnackbar('メッセージの送信に失敗しました', 'error');
    }
  };

  // メッセージ送信停止処理
  const handleStopMessage = useCallback(async () => {
    if (!session?.id) {
      showSnackbar('セッションが見つかりません', 'error');
      return;
    }

    try {
      const success = await abortMessage(session.id);
      
      if (success) {
        showSnackbar('メッセージ送信を停止しました', 'error');
      } else {
        showSnackbar('停止する対象のメッセージがありません', 'error');
      }
    } catch (error) {
      console.error('Failed to stop message:', error);
      showSnackbar('メッセージ送信の停止に失敗しました', 'error');
    }
  }, [abortMessage, session?.id, showSnackbar]);

  // やり直し処理
  const handleRetryAbortedMessage = useCallback(async (messageId: number) => {
    if (!session?.id || !selectedExpert) {
      showSnackbar('セッションまたは専門家が見つかりません', 'error');
      return;
    }

    try {
      await retryAbortedMessage(messageId, selectedExpert, session.id);
      showSnackbar('メッセージを再送信しました');
    } catch (error) {
      console.error('Failed to retry aborted message:', error);
      showSnackbar('メッセージの再送信に失敗しました', 'error');
    }
  }, [retryAbortedMessage, session?.id, selectedExpert, showSnackbar]);

  // 初期専門家設定
  useEffect(() => {
    if (initialExpertId && displayExperts.length > 0) {
      const expert = displayExperts.find(e => e.id === initialExpertId);
      if (expert) {
        setSelectedExpert(expert);
      }
    }
  }, [initialExpertId, displayExperts]);

  // 選択されたセッションの履歴をロード（リセット中は無効化）
  useEffect(() => {
    const resetParam = searchParams.get('reset');
    if (!selectedSessionId || resetParam === 'true') return;
    
    const loadData = async () => {
      try {
        // セッション情報を読み込み
        await loadChatHistory(selectedSessionId);
        // メッセージ履歴も読み込み
        await loadMessages(selectedSessionId);
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };
    
    loadData();
  }, [selectedSessionId, searchParams]); // 関数オブジェクトを依存から除去済み

  // セッション情報から専門家を設定（リセット中は無効化）
  useEffect(() => {
    const resetParam = searchParams.get('reset');
    if (session && session.expertId && resetParam !== 'true') {
      const expert = displayExperts.find(e => e.id === session.expertId);
      if (expert && expert.id !== selectedExpert?.id) {
        setSelectedExpert(expert);
      }
    }
  }, [session, displayExperts, selectedExpert, searchParams]);

  // エラー表示の自動クリア
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // 専門家AI取得エラーの自動クリア
  useEffect(() => {
    if (expertsError) {
      const timer = setTimeout(() => {
        clearExpertsError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [expertsError, clearExpertsError]);

  // 新しいチャット開始時の完全リセット
  useEffect(() => {
    const resetParam = searchParams.get('reset');
    
    if (resetParam === 'true') {
      // 非同期でリセット処理を実行
      const executeReset = async () => {
        // 専門家をリセット
        setSelectedExpert(null);
        
        // チャット状態を完全にクリア
        clearError();
        clearMessages();
        
        // 親コンポーネントにセッションIDのリセットを通知
        if (onSessionChange) {
          onSessionChange(null);
        }
        
        // 少し待ってからURLパラメータをクリア（状態の更新を確実にするため）
        setTimeout(() => {
          searchParams.delete('reset');
          setSearchParams(searchParams);
          
          showSnackbar('新しいチャットを開始します。専門家を選択してください。');
        }, 100);
      };
      
      executeReset();
    }
  }, [searchParams, setSearchParams, clearError, clearMessages, onSessionChange]);

  // 表示状態の変更を親に通知
  useEffect(() => {
    if (onViewChange) {
      onViewChange(selectedExpert ? 'chat' : 'welcome');
    }
  }, [selectedExpert, onViewChange]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* ヘッダー：専門家選択 - 専門家が選択されている時のみ表示 */}
      {selectedExpert && (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <AISelector
            selectedExpert={selectedExpert}
            isLoading={isLoading}
          />
        </Paper>
      )}

      {/* エラー表示 */}
      {error && (
        <Alert 
          severity="error" 
          onClose={clearError}
          sx={{ m: 1 }}
        >
          {error.message}
        </Alert>
      )}

      {/* 専門家AI取得エラー表示 */}
      {expertsError && (
        <Alert 
          severity="warning" 
          onClose={clearExpertsError}
          sx={{ m: 1 }}
        >
          専門家AIの読み込みに失敗しました。フォールバックデータを使用しています: {expertsError}
        </Alert>
      )}

      {/* 営業実績・経験情報入力促進バナー - ウェルカム画面・チャット画面共通で表示 */}
      <SalesProfilePromptBanner />

      {/* メインチャットエリア */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {expertsLoading ? (
          /* 専門家AI読み込み中の表示 */
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="textSecondary">
              専門家AIを読み込んでいます...
            </Typography>
          </Box>
        ) : selectedExpert ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {/* セッション情報表示 */}
            {session && (
              <Box
                sx={{
                  p: 1,
                  backgroundColor: theme.palette.grey[50],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  {session.title} | 開始: {new Date(session.createdAt).toLocaleString('ja-JP')}
                </Typography>
              </Box>
            )}

            {/* メッセージリスト - フレックス拡張でスペース埋める */}
            <Box sx={{ 
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MessageList
                messages={messages}
                expert={selectedExpert}
                isLoading={isLoading}
                isTyping={isTyping}
                isRegenerating={regeneratingMessageId !== undefined}
                regeneratingMessageId={regeneratingMessageId}
                onRetry={handleRetryMessage}
                onRetryAborted={handleRetryAbortedMessage}
                messageStatuses={messageStatuses}
                inputHeight={inputHeight}
              />
            </Box>

            {/* メッセージ入力 - 下部固定 */}
            <Box 
              sx={{ 
                position: 'sticky',
                bottom: 0,
                p: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 -2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)'}`,
                zIndex: 1000,
                flexShrink: 0, // サイズ固定
              }}
            >
              <MessageInput
                onSendMessage={handleSendMessage}
                onStopMessage={handleStopMessage}
                isLoading={isLoading}
                isAborting={isAborting}
                disabled={!selectedExpert}
                placeholder={`${selectedExpert.name}にメッセージを送信...`}
                onHeightChange={setInputHeight}
              />
            </Box>
          </Box>
        ) : (
          /* 専門家未選択時の表示 - ウェルカム画面（フルスクリーン） */
          <Box 
            sx={{ 
              flex: 1, 
              overflow: 'auto',
              // ウェルカム画面が画面全体を使用できるよう調整
              height: '100%',
            }}
          >
            <WelcomeScreen
              experts={displayExperts}
              onExpertSelect={handleExpertSelect}
            />
          </Box>
        )}
      </Box>

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};